/**
 * Collapsible "Attachments" section that aggregates every attachment across
 * the conversation's messages (mirrors the reference's Attachments accordion).
 * Box-style grid UI — no image previews, just file-type icons.
 * Clickable to open the AttachmentViewer slide-in panel.
 */
import { useMemo, useState, type JSX } from 'react'
import {
  ChevronDown,
  FileText,
  FileImage,
  FileVideo,
  FileAudio,
  FileArchive,
  FileSpreadsheet,
  FileType,
  Paperclip,
} from 'lucide-react'
import type { Message, MessageAttachment } from '../../../modules/conversations/types.js'
import { R } from './primitives.js'

function formatSize(bytes: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

type IconConfig = { icon: typeof FileText; bg: string; text: string; label: string }

function getFileConfig(mimeType: string): IconConfig {
  if (mimeType.startsWith('image/'))
    return { icon: FileImage, bg: 'bg-sky-50', text: 'text-sky-500', label: 'Image' }
  if (mimeType.startsWith('video/'))
    return { icon: FileVideo, bg: 'bg-purple-50', text: 'text-purple-500', label: 'Video' }
  if (mimeType.startsWith('audio/'))
    return { icon: FileAudio, bg: 'bg-indigo-50', text: 'text-indigo-500', label: 'Audio' }
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('7z') || mimeType.includes('tar'))
    return { icon: FileArchive, bg: 'bg-orange-50', text: 'text-orange-500', label: 'Archive' }
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv'))
    return { icon: FileSpreadsheet, bg: 'bg-emerald-50', text: 'text-emerald-500', label: 'Sheet' }
  if (mimeType.startsWith('text/') || mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('word'))
    return { icon: FileType, bg: 'bg-rose-50', text: 'text-rose-500', label: 'Document' }
  return { icon: FileText, bg: 'bg-slate-50', text: 'text-slate-400', label: 'File' }
}

export interface AttachmentsSectionProps {
  messages: Message[]
  conversationAttachments?: MessageAttachment[]
  onSelect?: (index: number) => void
}

export default function AttachmentsSection({ messages, conversationAttachments = [], onSelect }: AttachmentsSectionProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const messageAttachments = useMemo(() => messages.flatMap((m) => m.attachments), [messages])
  const attachments = useMemo(() => {
    const seen = new Set<string>()
    const all: (MessageAttachment & { url?: string })[] = []
    for (const a of messageAttachments) {
      if (!seen.has(a.id)) { seen.add(a.id); all.push(a) }
    }
    for (const a of conversationAttachments) {
      if (!seen.has(a.id)) { seen.add(a.id); all.push(a) }
    }
    return all
  }, [messageAttachments, conversationAttachments])

  return (
    <div className="shrink-0 border-b border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-5 py-3 text-left transition-colors hover:bg-slate-50/40"
      >
        <span className="flex items-center gap-2.5 text-[13px] font-semibold text-slate-700">
          <Paperclip className="h-4 w-4 text-slate-400" />
          Attachments
          {attachments.length > 0 && (
            <span className={`${R} bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-500`}>
              {attachments.length}
            </span>
          )}
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 pt-0.5">
          {attachments.length === 0 ? (
            <div className="flex items-center gap-2 py-4 text-xs text-slate-400">
              <Paperclip className="h-3.5 w-3.5" />
              No attachments
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2.5">
              {attachments.map((a, i) => {
                const cfg = getFileConfig(a.mimeType)
                const Icon = cfg.icon
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => onSelect?.(i)}
                    className={`group flex items-center gap-2.5 ${R} border border-slate-200 bg-white px-2.5 py-2.5 transition-all duration-200 hover:border-[#3366FF]/30 hover:bg-[#F8FAFF] hover:shadow-[0_1px_4px_rgba(0,0,0,0.04)]`}
                    title={a.filename}
                  >
                    <div className={`flex h-8 w-8 shrink-0 items-center justify-center ${R} ${cfg.bg} transition-colors duration-200`}>
                      <Icon className={`h-4 w-4 ${cfg.text}`} strokeWidth={1.8} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="block w-full truncate text-[11px] font-semibold leading-tight text-slate-700">
                        {a.filename}
                      </span>
                      <span className="mt-0.5 block text-[9px] leading-tight text-slate-400">
                        {cfg.label}{a.sizeBytes > 0 ? ` · ${formatSize(a.sizeBytes)}` : ''}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
